// src/templates/requests/ProviderAdmin.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Star, Trash2, Search } from 'lucide-react';
import { requestsService } from '../../services/requestsService';

const ProviderAdmin = ({ bookId }) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadProviders();
  }, [bookId]);

  const loadProviders = async () => {
    setLoading(true);
    try {
      const list = await requestsService.getProviderProfiles?.(bookId) ?? [];
      setProviders(list);
    } catch (error) {
      console.error('Failed to load providers', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVerify = async (provider) => {
    setSavingId(provider.id);
    try {
      await requestsService.updateProviderProfile(bookId, provider.id, { 
        ...provider, 
        verified: !provider.verified 
      });
      setProviders(prev => prev.map(p => 
        p.id === provider.id ? { ...p, verified: !p.verified } : p
      ));
    } catch (error) {
      console.error('Failed to update provider', error);
    } finally {
      setSavingId(null);
    }
  };

  const toggleFeatured = async (provider) => {
    setSavingId(provider.id);
    try {
      await requestsService.updateProviderProfile(bookId, provider.id, { 
        ...provider, 
        featured: !provider.featured 
      });
      setProviders(prev => prev.map(p => 
        p.id === provider.id ? { ...p, featured: !p.featured } : p
      ));
    } catch (error) {
      console.error('Failed to update provider', error);
    } finally {
      setSavingId(null);
    }
  };

  const removeProvider = async (provider) => {
    if (!window.confirm(`Remove ${provider.businessName}?`)) return;
    setSavingId(provider.id);
    try {
      await requestsService.deleteProviderProfile?.(bookId, provider.id);
      setProviders(prev => prev.filter(p => p.id !== provider.id));
    } catch (error) {
      console.error('Failed to remove provider', error);
    } finally {
      setSavingId(null);
    }
  };

  const filteredProviders = providers.filter(p => {
    const matchesSearch = p.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' ||
                         (filterStatus === 'verified' && p.verified) ||
                         (filterStatus === 'unverified' && !p.verified) ||
                         (filterStatus === 'featured' && p.featured);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Loading providers...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Provider Management</h2>
        <p className="text-sm text-slate-500 mt-1">Manage registered providers and their listings</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search providers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg"
        >
          <option value="all">All Providers</option>
          <option value="verified">Verified Only</option>
          <option value="unverified">Unverified Only</option>
          <option value="featured">Featured Only</option>
        </select>
      </div>

      {/* Providers List */}
      {filteredProviders.length === 0 ? (
        <div className="py-12 text-center text-slate-500">
          {providers.length === 0 ? 'No providers registered yet' : 'No providers match your filters'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProviders.map(provider => (
            <div 
              key={provider.id}
              className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Provider Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                      {provider.businessName}
                    </h3>
                    {provider.verified && (
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    )}
                    {provider.featured && (
                      <Star className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                    <div>{provider.category}</div>
                    {provider.email && <div>{provider.email}</div>}
                    {provider.phone && <div>{provider.phone}</div>}
                    <div className="text-xs text-slate-500">
                      Provider ID: {provider.id}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                  <button
                    onClick={() => toggleVerify(provider)}
                    disabled={savingId === provider.id}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                      provider.verified
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {provider.verified ? 'Verified' : 'Verify'}
                  </button>

                  <button
                    onClick={() => toggleFeatured(provider)}
                    disabled={savingId === provider.id}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                      provider.featured
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {provider.featured ? 'Featured' : 'Feature'}
                  </button>

                  <button
                    onClick={() => removeProvider(provider)}
                    disabled={savingId === provider.id}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderAdmin;